import { Component, Input, Output, EventEmitter, inject, Inject } from "@angular/core";

import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Message, MessageButton, MessageSelectList } from "../../../../core/models/message.model";
import {LucideAngularModule, List} from "lucide-angular";

@Component({
  selector: 'app-message-button',
  standalone: true,
  imports: [MatButtonModule, MatDialogModule, LucideAngularModule],
  styles: [`
    ::ng-deep .options-dialog .mat-mdc-dialog-surface {
      height: fit-content !important;
      max-height: 80vh !important;
      display: flex !important;
      flex-direction: column !important;
    }
  `],
  template: `
    <!-- Renderizar botones si existen -->
    @if (message.buttons && message.buttons.length > 0) {
      <div class="mt-3 flex flex-col md:flex-row md:flex-wrap gap-2">
        @for (button of message.buttons; track button.value) {
          <button 
            class="w-full md:w-auto px-4 py-3 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 transition-colors text-center font-medium md:min-w-[120px]"
            (click)="onButtonClick(button.value)">
            {{ button.label }}
          </button>
        }
      </div>
    }
    
    <!-- Renderizar select list como botón que abre dialog -->
    @if (message.selectList && message.selectList.options.length > 0) {
        <div class="mt-3">
            <button
                class="w-full md:w-auto px-4 py-3 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 transition-colors font-medium flex items-center justify-center gap-2 md:min-w-[200px]"
                (click)="openOptionsDialog(message.selectList)">
                
                <lucide-angular [img]="List" class="w-4 h-4"></lucide-angular>
                <span>{{ message.selectList.placeholder }}</span>
                
            </button>
        </div>
    }
  `
})
export class MessageButtonComponent {
  @Input() message!: Message;
  @Output() buttonClick = new EventEmitter<string>();
  
  private dialog = inject(MatDialog);
  readonly List = List;

  /**
   * Maneja el click en botones
   */
  onButtonClick(value: string): void {

    this.buttonClick.emit(value);
  }

  /**
   * Abre el dialog con las opciones de lista
   */
  openOptionsDialog(selectList: MessageSelectList): void {
    const dialogRef = this.dialog.open(OptionsDialogComponent, {
      width: 'fit-content',
      height: 'fit-content',
      minWidth: '280px',
      maxWidth: '90vw',
      maxHeight: '80vh',
      panelClass: 'options-dialog',
      data: {
        title: selectList.placeholder,
        options: selectList.options
      }
    });

    dialogRef.afterClosed().subscribe(selectedValue => {
      if (selectedValue) {
        this.buttonClick.emit(selectedValue);
      }
    });
  }
}

// Componente del Dialog
@Component({
  selector: 'app-options-dialog',
  standalone: true,
  imports: [MatButtonModule, MatDialogModule],
  template: `
    <div class="p-4 min-w-0">
      <h2 class="text-lg font-semibold mb-4 text-light-text dark:text-dark-text whitespace-nowrap">
        {{ data.title }}
      </h2>
      <div class="space-y-2">
        @for (option of data.options; track option.value) {
          <button
            class="w-full p-3 text-left rounded-lg border border-light-border dark:border-dark-border hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-500 transition-colors min-w-0"
            (click)="selectOption(option.value)">
            <div class="font-medium text-light-text dark:text-dark-text break-words">
              {{ option.label }}
            </div>
          </button>
        }
      </div>
      <div class="mt-4 flex justify-end">
        <button 
          mat-button 
          (click)="close()"
          class="text-light-text-secondary dark:text-dark-text-secondary">
          Cancelar
        </button>
      </div>
    </div>
  `
})
export class OptionsDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<OptionsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { title: string; options: Array<{ label: string; value: string }> }
  ) {}

  selectOption(value: string): void {
    this.dialogRef.close(value);
  }

  close(): void {
    this.dialogRef.close();
  }
}